<template>
    <div class="magic-converter">
        <!-- 柔光背景 -->
        <div class="magic-bg"></div>

        <!-- 标题区 -->
        <div class="header-zone">
            <h1>✨ Excel 魔法转换器</h1>
            <p>将你的表格文件变成神秘的 JSON 数据咒语</p>
        </div>

        <!-- 主操作区 -->
        <div class="main-container">
            <!-- 待命状态 -->
            <div v-if="state === 'idle'" class="spell-card idle" @click="selectFile">
                <div class="icon">📜</div>
                <h2>召唤你的 Excel 文件</h2>
                <p>支持 .xlsx / .xls 格式</p>
                <input
                        type="file"
                        ref="fileInput"
                        @change="handleFile"
                        accept=".xlsx, .xls"
                        hidden
                />
            </div>

            <!-- 转换中 -->
            <div v-else-if="state === 'converting'" class="spell-card casting">
                <div class="rune-spin">🌀</div>
                <h2>正在施展数据变形术...</h2>
                <p>请稍候，魔法正在进行中</p>
            </div>

            <!-- 成功 -->
            <div v-else-if="state === 'success'" class="spell-card success">
                <div class="rune-check">✅</div>
                <h2>转换成功！</h2>
                <p>JSON 咒语已生成，点击复制到剪贴板</p>
                <pre class="json-output" @click="copyJSON">{{ jsonString }}</pre>
                <button @click="downloadJSON" class="btn-download">💾 下载为 .json</button>
                <button @click="reset" class="btn-reset">🔄 重新开始</button>
            </div>

            <!-- 错误 -->
            <div v-else-if="state === 'error'" class="spell-card error">
                <div class="rune-error">❌</div>
                <h2>魔法失败</h2>
                <p>{{ errorMsg }}</p>
                <button @click="reset" class="btn-reset">🧙‍♂️ 重试一次</button>
            </div>
        </div>

        <!-- 底部提示 -->
        <div class="footer-tip">
            <small>💡 支持多行数据自动转数组，表头即为 key 名</small>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>📊 Excel 转 JSON 在线工具</h2>
            <p>将 Excel 表格数据（.xlsx/.xls）快速转换为 JSON 格式。支持多 Sheet、自动识别表头、数据类型转换，方便开发者对接 API、前端渲染数据。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>格式广泛</strong>：支持 .xlsx 和 .xls 格式</li>
                <li><strong>智能识别</strong>：自动识别表头作为 JSON 键名</li>
                <li><strong>多 Sheet</strong>：支持含多个工作表的 Excel 文件</li>
                <li><strong>一键导出</strong>：复制到剪贴板或下载 .json 文件</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>准备 Excel 文件，第一行为表头（字段名）</li>
                <li>上传 Excel 文件</li>
                <li>系统自动转换为 JSON 格式</li>
                <li>复制 JSON 文本或下载文件</li>
            </ol>

            <h3>💡 转换示例</h3>
            <p><strong>Excel 输入：</strong></p>
            <pre>姓名 | 年龄 | 城市
张三 | 25   | 北京
李四 | 30   | 上海</pre>
            <p><strong>JSON 输出：</strong></p>
            <pre>[{"姓名":"张三","年龄":25,"城市":"北京"},
 {"姓名":"李四","年龄":30,"城市":"上海"}]</pre>

            <h3>🔒 隐私说明</h3>
            <p>使用 SheetJS 在浏览器本地解析，Excel 数据不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
import seoMixin from '../js/seo-mixin';
    // 使用 xlsx.js（SheetJS）进行 Excel 解析
    import * as XLSX from 'xlsx'

    export default {
        name: 'ExcelToJSON',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: 'Excel转JSON在线工具 - FreeToolBox',
                seoDescription: '免费在线Excel转JSON工具，将Excel表格数据(.xlsx/.xls)转换为JSON格式。浏览器本地处理，支持多行数据自动转数组，下载为.json文件。',
                seoKeywords: 'excel转json,excel转json工具,在线excel转换,表格转json,xlsx转json',
                state: 'idle', // idle, converting, success, error
                jsonData: [],
                errorMsg: '',
                jsonString: ''
            }
        },
        methods: {
            selectFile() {
                this.$refs.fileInput.click()
            },

            handleFile(e) {
                const file = e.target.files[0]
                if (!file) return

                this.state = 'converting'

                // 使用 FileReader 异步读取
                const reader = new FileReader()
                reader.onload = (event) => {
                    try {
                        const data = new Uint8Array(event.target.result)
                        const workbook = XLSX.read(data, { type: 'array' })

                        // 取第一个工作表
                        const sheetName = workbook.SheetNames[0]
                        const worksheet = workbook.Sheets[sheetName]

                        // 转成 JSON（数组 of 对象）
                        const json = XLSX.utils.sheet_to_json(worksheet, { raw: true })

                        if (json.length === 0) {
                            throw new Error('表格为空，请检查内容')
                        }

                        this.jsonData = json
                        this.jsonString = JSON.stringify(json, null, 2)
                        this.state = 'success'
                    } catch (err) {
                        this.errorMsg = err.message || '文件解析失败'
                        this.state = 'error'
                    }
                }

                reader.onerror = () => {
                    this.errorMsg = '文件读取失败'
                    this.state = 'error'
                }

                reader.readAsArrayBuffer(file)
            },

            async copyJSON() {
                try {
                    await navigator.clipboard.writeText(this.jsonString)
                    this.showToast('✅ 已复制到剪贴板', 'success')
                } catch (err) {
                    this.showToast('👆 请手动长按复制', 'warning')
                }
            },

            downloadJSON() {
                const blob = new Blob([this.jsonString], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'converted_data.json'
                a.click()
                URL.revokeObjectURL(url)
            },

            reset() {
                this.state = 'idle'
                this.jsonData = []
                this.jsonString = ''
                this.$refs.fileInput.value = '' // 清空文件选择
            },

            showToast(msg, type) {
                const old = document.querySelector('.magic-toast')
                if (old) old.remove()

                const toast = document.createElement('div')
                toast.className = 'magic-toast'
                toast.textContent = msg
                toast.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: ${type === 'success' ? '#52c41a' : '#faad14'};
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transition: opacity 0.3s;
        text-align: center;
      `
                document.body.appendChild(toast)
                setTimeout(() => {
                    toast.style.opacity = '0'
                    setTimeout(() => toast.remove(), 300)
                }, 2000)
            }
        }
    }
</script>

<style scoped>
    .magic-converter {
        min-height: 100vh;
        padding: 40px 20px;
        position: relative;
        font-family: 'Comic Sans MS', 'Segoe UI', cursive, sans-serif;
        color: #444;
        background: #f8f9ff;
    }

    /* 柔光背景 */
    .magic-bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background:
                radial-gradient(circle at 20% 30%, rgba(147, 112, 219, 0.1), transparent 40%),
                radial-gradient(circle at 80% 70%, rgba(72, 201, 176, 0.08), transparent 40%);
        z-index: -1;
    }

    /* 标题区 */
    .header-zone {
        text-align: center;
        margin-bottom: 40px;
    }

    .header-zone h1 {
        font-size: 2.5em;
        color: #5d40b8;
        margin: 0;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
    }

    .header-zone p {
        color: #666;
        margin-top: 8px;
        font-size: 1.1em;
    }

    /* 主容器 */
    .main-container {
        max-width: 800px;
        margin: 0 auto;
    }

    /* 魔法卡片 */
    .spell-card {
        background: white;
        border-radius: 20px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        padding: 40px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px dashed transparent;
    }

    .spell-card:hover {
        transform: translateY(-4px);
    }

    /* 待命状态 */
    .idle {
        border-color: #bbdefb;
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        cursor: pointer;
    }

    .idle .icon {
        font-size: 4em;
        margin-bottom: 16px;
    }

    /* 施法中 */
    .casting {
        background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
        animation: pulse 1.5s infinite;
    }

    .rune-spin {
        font-size: 3em;
        animation: spin 2s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(255, 165, 0, 0.4); }
        70% { box-shadow: 0 0 0 10px rgba(255, 165, 0, 0); }
        100% { box-shadow: 0 0 0 0 rgba(255, 165, 0, 0); }
    }

    /* 成功状态 */
    .success {
        background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
        cursor: default;
    }

    .rune-check {
        font-size: 3em;
        color: #52c41a;
    }

    /* 错误状态 */
    .error {
        background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
        cursor: default;
    }

    .rune-error {
        font-size: 3em;
        color: #f5222d;
    }

    /* JSON 输出框 */
    .json-output {
        background: #1e1e1e;
        color: #dcdcaa;
        padding: 16px;
        border-radius: 12px;
        font-family: 'Courier New', monospace;
        font-size: 14px;
        text-align: left;
        max-height: 300px;
        overflow-y: auto;
        margin: 20px 0;
        cursor: pointer;
        border: 1px solid #333;
    }

    .json-output:hover {
        box-shadow: 0 0 8px rgba(255,255,255,0.2);
    }

    /* 按钮 */
    .btn-download,
    .btn-reset {
        padding: 10px 20px;
        margin: 0 8px;
        border: none;
        border-radius: 12px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.3s;
    }

    .btn-download {
        background: #1890ff;
        color: white;
    }

    .btn-reset {
        background: #9e9e9e;
        color: white;
    }

    .btn-download:hover,
    .btn-reset:hover {
        transform: scale(1.05);
    }

    /* 底部提示 */
    .footer-tip {
        text-align: center;
        margin-top: 60px;
        color: #aaa;
        font-size: 0.9em;
    }

    /* 响应式 */
    @media (max-width: 480px) {
        .header-zone h1 {
            font-size: 2em;
        }
        .spell-card {
            padding: 30px 20px;
        }
        .json-output {
            font-size: 12px;
        }
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #4caf50;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
    .tool-content code {
        background: #e9ecef;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
    }
    .tool-content pre {
        background: #f4f4f4;
        padding: 15px;
        border-radius: 6px;
        overflow-x: auto;
        font-size: 0.9em;
    }
</style>